<template>
  <b-card no-body>
    <b-card-header class="d-flex justify-content-between align-items-center">
      <h4 class="mb-0">Дэшборд музыки</h4>
      <b-button variant="outline-primary" size="sm" :disabled="loading" @click="fetchDashboard">
        <feather-icon icon="RefreshCwIcon" class="mr-25" />
        Обновить
      </b-button>
    </b-card-header>

    <b-overlay :show="loading" rounded="sm">
      <div v-if="rows.length" class="table-responsive">
        <table class="table table-sm table-bordered mb-0 music-dashboard-table">
          <thead>
            <tr>
              <th>Показатель</th>
              <th v-for="column in columns" :key="column.key" class="text-center">
                {{ column.title }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.key">
              <th class="align-middle">{{ row.label }}</th>
              <td v-for="column in columns" :key="column.key" class="text-center align-middle">
                <div v-if="row.type === 'distribution'" class="rating-chart" :title="chartTitle(ratingValues(row, column))">
                  <div v-for="rating in ratings" :key="rating" class="rating-chart__column">
                    <span class="rating-chart__value" :class="{ 'font-weight-bold': isPriorityColumn(column) }">{{ ratingValues(row, column)[rating] || 0 }}</span>
                    <span class="rating-chart__bar" :style="{ height: `${ratingHeight(ratingValues(row, column), rating)}px` }" />
                    <span class="rating-chart__label">{{ rating }}</span>
                  </div>
                </div>
                <template v-else-if="row.percentOf">
                  <div><span class="metric-value" :class="{ 'font-weight-bold': isPriorityColumn(column) }">{{ row.values[column.key] || 0 }}</span> <small class="text-muted">{{ percentage(row, column) }}%</small></div>
                  <div class="percentage-bar"><span :style="{ width: `${percentage(row, column)}%` }" /></div>
                </template>
                <span v-else class="metric-value" :class="{ 'font-weight-bold': isPriorityColumn(column) }">{{ row.values[column.key] || 0 }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="!loading" class="text-center text-muted py-3">Нет данных</div>
    </b-overlay>
  </b-card>
</template>

<script>
import { BButton, BCard, BCardHeader, BOverlay } from 'bootstrap-vue'
import { onUnmounted, ref } from '@vue/composition-api'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import musicStoreModule from '@/views/db-music/musicStoreModule'

export default {
  components: { BButton, BCard, BCardHeader, BOverlay },
  setup() {
    const STORE_MODULE_NAME = 'music-dashboard'
    const toast = useToast()
    const columns = ref([])
    const rows = ref([])
    const loading = ref(false)
    const ratings = Array.from({ length: 10 }, (_, index) => index + 1)
    const priorityTitles = ['Поп', 'Рок', 'Русский Рок', 'Рэп']

    if (!store.hasModule(STORE_MODULE_NAME)) store.registerModule(STORE_MODULE_NAME, musicStoreModule)
    onUnmounted(() => {
      if (store.hasModule(STORE_MODULE_NAME)) store.unregisterModule(STORE_MODULE_NAME)
    })

    const fetchDashboard = () => {
      loading.value = true
      store.dispatch('music-dashboard/fetchDashboard')
        .then(response => {
          columns.value = (response.data.data.columns || []).sort((left, right) => {
            const position = column => {
              if (column.key === 'total') return 0
              const priorityIndex = priorityTitles.indexOf(column.title)
              return priorityIndex === -1 ? priorityTitles.length + 1 : priorityIndex + 1
            }
            return position(left) - position(right)
          })
          rows.value = response.data.data.rows || []
        })
        .catch(error => {
          toast({
            component: ToastificationContent,
            props: {
              title: error.response?.data?.message || 'Не удалось загрузить дэшборд музыки',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => {
          loading.value = false
        })
    }

    const ratingHeight = (values, rating) => {
      const maximum = Math.max(...ratings.map(item => values[item] || 0), 1)
      return Math.max(2, Math.round(((values[rating] || 0) / maximum) * 36))
    }

    const chartTitle = values => ratings.map(rating => `${rating}: ${values[rating] || 0}`).join('\n')
    const isPriorityColumn = column => priorityTitles.includes(column.title)
    const ratingValues = (row, column) => row.values[column.key] || {}
    const percentage = (row, column) => {
      const base = rows.value.find(item => item.key === row.percentOf)?.values[column.key] || 0
      return base ? Math.round(((row.values[column.key] || 0) / base) * 100) : 0
    }

    fetchDashboard()

    return {
      chartTitle, columns, fetchDashboard, isPriorityColumn, loading, percentage, ratingHeight, ratings, ratingValues, rows,
    }
  },
}
</script>

<style lang="scss" scoped>
.music-dashboard-table th:first-child {
  min-width: 220px;
}

.music-dashboard-table td {
  min-width: 112px;
}

.metric-value {
  font-size: 1.1rem;
}

.rating-chart {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  height: 58px;
}

.rating-chart__column {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 8px;
  height: 100%;
}

.rating-chart__value {
  font-size: 8px;
  line-height: 10px;
}

.rating-chart__bar {
  display: block;
  width: 7px;
  background: #7367f0;
  border-radius: 2px 2px 0 0;
}

.rating-chart__label {
  font-size: 8px;
  line-height: 10px;
}

.percentage-bar {
  width: 70px;
  height: 3px;
  margin: 2px auto 0;
  overflow: hidden;
  background: rgba(115, 103, 240, 0.15);
  border-radius: 2px;
}

.percentage-bar span {
  display: block;
  height: 100%;
  background: #7367f0;
}
</style>
